import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { getRtkErrorMessage } from 'api/getRtkErrorMessage';
import { Card, CascadeOption } from 'components/Card';
import DrawerExtraButtons from 'components/DrawerExtraButtons';
import { InlineOrganizationTreeSelect } from 'components/OrganizationTreeSelect';
import { usePermission } from 'hooks/usePermission';
import useSelectSearch from 'hooks/useSelectSearch';
import { startCase } from 'lodash';
import { UserObjects } from 'pages/userManagement/users/sections/UserObjects';
import { requestObjectTypes } from 'slices/objectTypes.slice';
import { fetchOrganizationHierarchy } from 'slices/organizationHierarchy.slice';
import {
  appPermissionsSelector,
  fetchPermissions,
  scopesSelector,
} from 'slices/permission.slice';
import { fetchRoles, rolesSelector } from 'slices/roles.slice';
import { useAppDispatch, useAppSelector } from 'slices/store';
import { errorNotification, successNotification } from 'utils/notifications';
import { EAppId, TAffiliation } from '@timeedit/types';
import { APP_ID_TO_NAME_MAP } from '@timeedit/types/lib/enums/appIds.enum';
import {
  Button,
  ConfirmationPopup,
  Icon,
  Select,
  TEDrawer,
  TEInput,
} from '@timeedit/ui-components';
import {
  Cascader,
  ConfigProvider,
  Flex,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Spin,
  Typography,
} from '@timeedit/ui-components/antd';
import {
  useCreateAffiliationMutation,
  useDeleteAffiliationMutation,
  useGetAffiliationQuery,
  usePatchAffiliationMutation,
} from '../api/authConfig.api';
import { buildAffiliationCopy } from './utils/buildAffiliationCopy';
import './AffiliationDrawer.scss';

type Props = {
  authConfigId: string;
  affiliationId: string;
  confirmDiscardAffiliationChanges: (actionFn: () => void) => void;
  isDirtyRef: React.MutableRefObject<boolean>;
};

const AffiliationDrawer: React.FC<Props> = ({
  authConfigId,
  affiliationId,
  confirmDiscardAffiliationChanges,
  isDirtyRef,
}: Props) => {
  const canEdit = usePermission({
    permissions: ['TE_ADMIN::manageAuthConfigs'],
  });
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const copyState = location.state as
    | { affiliation?: TAffiliation }
    | undefined;

  const { data: affiliation, isFetching } = useGetAffiliationQuery(
    {
      affiliationId,
      authConfigId,
    },
    { skip: !affiliationId || affiliationId === 'new' }
  );

  const [patchAffiliation] = usePatchAffiliationMutation();
  const [createAffiliation] = useCreateAffiliationMutation();
  const [deleteAffiliation] = useDeleteAffiliationMutation();

  const onClose = useCallback(
    () => navigate('', { relative: 'route' }),
    [navigate]
  );

  const onDelete = useCallback(async () => {
    if (affiliationId === 'new') return;
    const result = await deleteAffiliation({ authConfigId, affiliationId });
    if ('error' in result && result.error) {
      errorNotification('Failed to delete affiliation', [
        getRtkErrorMessage(result.error),
      ]);
      return;
    }
    successNotification('Affiliation deleted');
    onClose();
  }, [affiliationId, authConfigId, deleteAffiliation, onClose]);

  const onDuplicate = useCallback(() => {
    if (!affiliation) return;
    navigate('?id=new', {
      state: { affiliation: buildAffiliationCopy(affiliation) },
    });
  }, [affiliation, navigate]);

  useEffect(() => {
    dispatch(fetchOrganizationHierarchy());
    dispatch(fetchRoles());
    dispatch(fetchPermissions());
    dispatch(requestObjectTypes());
  }, []);

  const [form] = Form.useForm<TAffiliation>();

  const userObjects = Form.useWatch('userObjects', form);
  // Local mirror of the label field for the editable Typography in the
  // drawer title. The form remains the source of truth for submission;
  // this is just so the rendered title re-renders as the user types.
  const [labelValue, setLabelValue] = useState('');

  const onSubmit = useCallback(async () => {
    try {
      const isNew = affiliationId === 'new';
      await form.validateFields();
      // `validateFields()` only returns values for fields registered via
      // Form.Item. The `label` field is written via setFieldValue from the
      // editable title (no Form.Item to avoid antd's circular-ref warning),
      // so we pull the full store snapshot to include it. `authConfigId` is
      // route state, not part of the mutation body.
      const { authConfigId: _, ...affiliationPayload } =
        form.getFieldsValue(true);

      if (isNew) {
        const result = await createAffiliation({
          authConfigId: authConfigId,
          affiliation: affiliationPayload,
        });
        if (result.error) {
          throw new Error(getRtkErrorMessage(result.error));
        }
        successNotification('Affiliation created successfully');
      } else {
        const result = await patchAffiliation({
          authConfigId: authConfigId,
          affiliationId: affiliationId,
          affiliation: affiliationPayload,
        });

        if (result.error) {
          throw new Error(getRtkErrorMessage(result.error));
        }

        successNotification('Affiliation updated successfully');
      }

      onClose();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : typeof error === 'object' &&
              error !== null &&
              'errorFields' in error
            ? (
                error as { errorFields: Array<{ errors?: string[] }> }
              ).errorFields.map((e) => e.errors?.join(', ') ?? '')
            : String(error);
      errorNotification(
        'Failed to save affiliation',
        Array.isArray(message) ? message : [message]
      );
    }
  }, [
    form,
    affiliationId,
    authConfigId,
    createAffiliation,
    patchAffiliation,
    onClose,
  ]);

  const isNew = affiliationId === 'new';
  const initialValues = useMemo(
    () =>
      buildAffiliation(
        isNew
          ? { authConfigId, ...(copyState?.affiliation ?? {}) }
          : { authConfigId, ...affiliation }
      ),
    [affiliation, authConfigId, isNew]
  );

  // Resync the form to the current source-of-truth values. Called from the
  // effect below (when the selected affiliation changes) and from Discard.
  // antd reads `initialValues` only on mount, so this is the only path back
  // to a clean state for both modes.
  const resyncForm = useCallback(() => {
    form.resetFields();
    form.setFieldsValue(initialValues);
    setLabelValue(initialValues.label ?? '');
  }, [form, initialValues]);

  useEffect(() => {
    resyncForm();
  }, [resyncForm]);

  // `isFieldsTouched()` only iterates fields registered via Form.Item.
  // The label is written via setFieldValue from the editable title and has
  // no Form.Item (see the comment in onSubmit), so it's invisible to
  // antd's tracking — we compare its value to the baseline explicitly.
  Form.useWatch([], form);
  const isDirty =
    form.isFieldsTouched() ||
    (form.getFieldValue('label') ?? '') !== (initialValues.label ?? '');
  isDirtyRef.current = isDirty;

  const handleDiscard = useCallback(() => {
    resyncForm();
    isDirtyRef.current = false;
  }, [resyncForm, isDirtyRef]);
  const content = useMemo(() => {
    if (isFetching) return <Spin size="small" />;
    if (!affiliation && !isNew) return <>{'Affiliation not found'}</>;

    return (
      <Form
        form={form}
        initialValues={initialValues}
        component="div"
        disabled={!canEdit}
      >
        <Flex vertical gap="middle">
          <Card
            title="Attribute Matching Criteria"
            tooltip="List of SAML response attributes that need to match for this affiliation. Can be OID URNs, Claim URIs, LDAP/eduPerson attributes, and more. For example, `eduPersonAffiliation`, `https://schemas.xmlsoap.org/...`, `urn:oid:1.3.6.1.4.1...`"
          >
            <EditAttributeMatchingCriteria form={form} />
          </Card>

          <Form.Item
            name="priority"
            label="Prioritisation"
            tooltip="When a user logs in, the system checks their attributes against each affiliation's matching criteria. If multiple affiliations match, the one with the highest priority is used. Set this to a number to determine the order of evaluation; higher numbers are checked first. Leave blank for lowest priority."
            labelCol={{ flex: '0 0 192px' }}
            wrapperCol={{ flex: '1 1 auto' }}
            labelAlign="left"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="standardOrganization"
            label="Standard Organization"
            tooltip="Select the standard organization for this affiliation."
            labelCol={{ flex: '0 0 192px' }}
            wrapperCol={{ flex: '1 1 auto' }}
            labelAlign="left"
          >
            <InlineOrganizationTreeSelect
              allowClear
              placeholder="Select"
              dataTestId="standard-org"
              onlyLeafNodes
            />
          </Form.Item>

          <Card title="Organization nodes and Roles">
            <OrganizationsAndRoles form={form} />
          </Card>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '20px',
            }}
          >
            <Scopes form={form} />
            <AppPermissions form={form} />
          </div>
          <ConfigProvider componentDisabled={false}>
            <Form.Item name="userObjects" noStyle>
              <UserObjects
                profile={{
                  id: affiliationId,
                  userObjects,
                }}
                onChange={(_key, userObjects) => {
                  form.setFieldValue('userObjects', userObjects);
                }}
                disabled={!canEdit}
              />
            </Form.Item>
          </ConfigProvider>
        </Flex>
      </Form>
    );
  }, [
    affiliation,
    isFetching,
    form,
    userObjects,
    affiliationId,
    isNew,
    initialValues,
    canEdit,
  ]);

  return (
    <TEDrawer
      expandWidth="50vw"
      defaultExpand
      firstDrawer={{
        content,
        drawerOpen: !!affiliationId,
        // `TEDrawer.firstDrawer.title` is typed as `string` but the underlying
        // DrawerTitle renders it as children, so JSX is rendered correctly at
        // runtime. Casting through `unknown` to bypass the library's
        // narrower typing.
        title: (
          <div className="affiliation-drawer-title">
            <Typography.Text
              editable={
                canEdit
                  ? {
                      onChange: (value) => {
                        form.setFieldValue('label', value);
                        setLabelValue(value);
                      },
                      tooltip: false,
                    }
                  : false
              }
            >
              {labelValue || (isNew ? 'New affiliation' : 'Affiliation')}
            </Typography.Text>
          </div>
        ) as unknown as string,
        extra: (
          <DrawerExtraButtons
            handleDiscard={handleDiscard}
            handleSave={onSubmit}
            isSaveDisable={!canEdit || !isDirty}
            buttonSize="middle"
          />
        ),
        leftExtra: (
          <>
            <Button
              onClick={onDuplicate}
              size="small"
              disabled={!canEdit || isNew}
              icon={<Icon id="CopyOutlined" alt="Duplicate" />}
            >
              Duplicate
            </Button>
            <ConfirmationPopup
              message="Are you sure you want to delete this affiliation?"
              onConfirm={onDelete}
              okText="Delete"
              cancelText="Cancel"
            >
              <Button
                disabled={!canEdit || isNew}
                size="small"
                icon={<Icon id="DeleteOutlined" alt="Delete" />}
              >
                Delete
              </Button>
            </ConfirmationPopup>
          </>
        ),
        setDrawerOpen: (open) =>
          !open && confirmDiscardAffiliationChanges(onClose),
      }}
    />
  );
};

const Scopes = ({ form }: { form: FormInstance<TAffiliation> }) => {
  const scopes = Form.useWatch('scopes', form);

  const allScopes = useAppSelector(scopesSelector);

  const appNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    Object.keys(APP_ID_TO_NAME_MAP).forEach((key) => {
      map[key] =
        APP_ID_TO_NAME_MAP[key as EAppId] ||
        startCase(key.replace(/TE_/g, '').toLowerCase());
    });
    return map;
  }, []);

  const unassignableScopes = new Set(['TE_VIEWER::readOnly']);

  const isScopeAssignable = (scope: string) => {
    // Check if the scope is part of the set of unassignable scopes or already added
    if (unassignableScopes.has(scope) || scopes?.includes(scope)) {
      return false;
    }

    return true;
  };

  const formattedScopes: CascadeOption[] = Object.values(
    allScopes.reduce<Record<string, CascadeOption>>((acc, scope) => {
      const [app, level] = scope.value.split('::');

      acc[app] = {
        value: app,
        label: appNameMap[app] || startCase(app.toLocaleLowerCase()),
        children: [
          ...(acc[app]?.children || []),
          {
            value: scope.value,
            label: level,
            disabled: !isScopeAssignable(scope.value),
          },
        ],
      };

      return acc;
    }, {})
  );

  return (
    <Card title="Scopes">
      <Flex
        vertical
        gap="small"
        style={{ minHeight: '150px', justifyContent: 'space-between' }}
      >
        <div>
          <Form.List name="scopes">
            {(fields, { remove }) =>
              fields.map(({ key, name }) => (
                <Flex
                  gap="small"
                  style={{ alignItems: 'end', justifyContent: 'space-between' }}
                  wrap
                  key={key}
                >
                  <Form.Item name={[name]}>
                    <Typography.Text>{scopes?.[name]}</Typography.Text>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      onClick={() => remove(name)}
                      type="text"
                      icon={<Icon id="DeleteOutlined" alt="Delete" />}
                    />
                  </Form.Item>
                </Flex>
              ))
            }
          </Form.List>
        </div>
        <div>
          <Cascader
            style={{ paddingTop: '16px' }}
            options={formattedScopes}
            onChange={(val) =>
              form.setFields([
                {
                  name: 'scopes',
                  value: [...(scopes ?? []), val[1].toString()],
                  touched: true,
                },
              ])
            }
          >
            <Button type="dashed" size="small">
              Add
            </Button>
          </Cascader>
        </div>
      </Flex>
    </Card>
  );
};

const AppPermissions = ({ form }: { form: FormInstance<TAffiliation> }) => {
  const appPermissions = Form.useWatch('appPermissions', form);

  const allAppPermissions = useAppSelector(appPermissionsSelector);

  const appNameMap = useMemo(() => {
    const map: { [key: string]: string } = {};
    Object.keys(APP_ID_TO_NAME_MAP).forEach((key) => {
      map[key] =
        APP_ID_TO_NAME_MAP[key as EAppId] ||
        startCase(key.replace(/TE_/g, '').toLowerCase());
    });
    return map;
  }, []);

  const formattedAppPermissions: CascadeOption[] = Object.values(
    allAppPermissions.reduce(
      (acc, appPermission) => {
        const [app, level] = appPermission.value.split('::');
        if (!level) return acc;
        return {
          ...acc,
          [app]: {
            value: app,
            label: appNameMap[app],
            children: [
              ...(acc[app]?.children || []),
              {
                value: appPermission.value,
                label: level,
                disabled: appPermissions?.includes(appPermission.value),
              },
            ],
          },
        };
      },
      {} as Record<string, any>
    )
  );

  return (
    <Card title="App permissions">
      <Flex
        vertical
        gap="small"
        style={{
          minHeight: '150px',
          justifyContent: 'space-between',
          height: '100%',
        }}
      >
        <div>
          <Form.List name="appPermissions">
            {(fields, { remove }) =>
              fields.map(({ key, name }) => (
                <Flex
                  gap="small"
                  style={{ alignItems: 'end', justifyContent: 'space-between' }}
                  wrap
                  key={key}
                >
                  <Form.Item name={[name]}>
                    <Typography.Text>{appPermissions?.[name]}</Typography.Text>
                  </Form.Item>
                  <Form.Item>
                    <Button
                      onClick={() => remove(name)}
                      type="text"
                      icon={<Icon id="DeleteOutlined" alt="Delete" />}
                    />
                  </Form.Item>
                </Flex>
              )) || <div />
            }
          </Form.List>
        </div>
        <div>
          <Cascader
            style={{ paddingTop: '16px' }}
            options={formattedAppPermissions}
            onChange={(val) =>
              form.setFields([
                {
                  name: 'appPermissions',
                  value: [...(appPermissions ?? []), val[1].toString()],
                  touched: true,
                },
              ])
            }
          >
            <Button type="dashed" size="small">
              Add
            </Button>
          </Cascader>
        </div>
      </Flex>
    </Card>
  );
};

const OrganizationsAndRoles = ({
  form,
}: {
  form: FormInstance<TAffiliation>;
}) => {
  const organizationsAndRoles = Form.useWatch('organizationsAndRoles', form);
  const allRoles = useAppSelector(rolesSelector);

  const [roles, setRoleFilter] = useSelectSearch(allRoles, 'name', 'name');

  return (
    <>
      <Form.List name="organizationsAndRoles">
        {(fields, { remove }) => {
          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1fr',
                gap: '5px',
                padding: '8px',
                paddingBottom: '16px',
              }}
            >
              <TEInput.LabelWrapper
                wrapperStyle={{ flexGrow: 1 }}
                label="Organization"
                flow="column"
              />
              <TEInput.LabelWrapper
                wrapperStyle={{ flexGrow: 1 }}
                label="Role"
                flow="column"
              />
              {fields.map(({ key, name }) => (
                <Fragment key={key}>
                  <Form.Item
                    name={[name, 'organizationId']}
                    noStyle
                    rules={[
                      { required: true, message: 'Organization is required' },
                    ]}
                  >
                    <InlineOrganizationTreeSelect
                      allowClear
                      placeholder="Select"
                    />
                  </Form.Item>

                  <Flex gap="small" style={{ alignItems: 'center' }}>
                    <Form.Item
                      name={[name, 'role']}
                      noStyle
                      rules={[{ required: true, message: 'Role is required' }]}
                    >
                      <Select
                        style={{ width: '100%' }}
                        showSearch
                        onSearch={setRoleFilter}
                        options={roles}
                        filterOption={false}
                        onBlur={() => setRoleFilter('')}
                      />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        size="small"
                        onClick={() => remove(name)}
                        type="text"
                        icon={<Icon id="DeleteOutlined" alt="Delete" />}
                      />
                    </Form.Item>
                  </Flex>
                </Fragment>
              ))}
            </div>
          );
        }}
      </Form.List>
      <div>
        <Button
          type="dashed"
          size="small"
          onClick={() =>
            form.setFieldValue('organizationsAndRoles', [
              ...(organizationsAndRoles ?? []),
              undefined,
            ])
          }
        >
          Add
        </Button>
      </div>
    </>
  );
};

const EditAttributeMatchingCriteria = ({
  form,
}: {
  form: FormInstance<TAffiliation>;
}) => {
  const matchingCriteria = Form.useWatch('matchingCriteria', form);

  return (
    <>
      <Form.List name="matchingCriteria">
        {(fields, { remove }) => {
          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '5px',
                padding: '8px',
                paddingBottom: '16px',
              }}
            >
              <TEInput.LabelWrapper
                wrapperStyle={{ flexGrow: 1 }}
                label="Attribute"
                flow="column"
              />
              <TEInput.LabelWrapper
                wrapperStyle={{ flexGrow: 1 }}
                label="Value"
                flow="column"
              />
              {fields.map(({ key, name }) => (
                <Fragment key={key}>
                  <Form.Item
                    name={[name, 'attribute']}
                    noStyle
                    rules={[
                      { required: true, message: 'Attribute is required' },
                    ]}
                  >
                    <Input />
                  </Form.Item>

                  <Flex gap="small" style={{ alignItems: 'center' }}>
                    <Form.Item
                      name={[name, 'value']}
                      noStyle
                      rules={[{ required: true, message: 'Value is required' }]}
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        onClick={() => remove(name)}
                        type="text"
                        icon={<Icon id="DeleteOutlined" alt="Delete" />}
                        size="small"
                      />
                    </Form.Item>
                  </Flex>
                </Fragment>
              ))}
            </div>
          );
        }}
      </Form.List>
      <div>
        <Button
          type="dashed"
          size="small"
          onClick={() =>
            form.setFieldValue('matchingCriteria', [
              ...(matchingCriteria ?? []),
              undefined,
            ])
          }
        >
          Add
        </Button>
      </div>
    </>
  );
};

export default AffiliationDrawer;

const buildAffiliation = (data: Partial<TAffiliation> = {}): TAffiliation => ({
  authConfigId: data.authConfigId ?? '',
  label: data.label ?? '',
  priority: data.priority,
  matchingCriteria: data.matchingCriteria ?? [],
  organizationsAndRoles: data.organizationsAndRoles ?? [],
  scopes: data.scopes ?? [],
  appPermissions: data.appPermissions ?? [],
  userObjects: data.userObjects ?? [],
  standardOrganization: data.standardOrganization ?? '',
});
